import { Badge } from '../../atoms/Badge/Badge';
import './EntryPhotoPreview.css';

/**
 * The image an entry preview shows. Matches the journal list's `coverImage`
 * (JournalCoverImage) and the community feed's `attachedImages[n]`.
 */
export interface EntryPhotoPreviewImage {
  thumbnailUrl: string | null;
  displayUrl: string | null;
  /** The image's note — the alt text when present (journal owner only; null in the feed). */
  note?: string | null;
  width?: number | null;
  height?: number | null;
}

/** The URL the preview loads: the small thumbnail variant, else the display variant. */
export function entryPhotoSrc(image: EntryPhotoPreviewImage | null | undefined): string | null {
  if (!image) return null;
  return image.thumbnailUrl ?? image.displayUrl ?? null;
}

/**
 * Class for the card root while it shows a preview photo: photo on the left,
 * body on the right, stacked (photo on top) on narrow screens. Put
 * ENTRY_PHOTO_BODY_CLASS on the body element.
 */
export const ENTRY_PHOTO_HOST_CLASS = 'rg-entry-photo-host';
export const ENTRY_PHOTO_BODY_CLASS = 'rg-entry-photo-host__body';

export interface EntryPhotoPreviewProps {
  image: EntryPhotoPreviewImage;
  /**
   * Total number of attached photos. When > 1 a "+N" badge marks the others;
   * the screen-reader text (`countLabel`) names the total. Omit it to show
   * just the photo (journal list).
   */
  count?: number;
  /** Screen-reader text for `count`, e.g. "3 photos attached" (required with count). */
  countLabel?: string;
  'data-testid'?: string;
}

/**
 * The entry preview photo shared by the journal list (JournalEntryCard) and
 * the community feed (CommunityFeedItem) — promoted from the journal card so
 * both look identical (CLAUDE.md §1a). A fixed token-sized square on the left
 * (16:9, full width, on top at ≤ 480 px), lazily loaded so the card never
 * shifts. The title link sits right next to the photo, so without a note the
 * image is decorative (alt="") rather than a repeat of the title. Not
 * interactive: the title link opens the entry.
 *
 * Callers render it only when entryPhotoSrc(image) is non-null.
 */
export function EntryPhotoPreview({ image, count, countLabel, 'data-testid': testId }: EntryPhotoPreviewProps) {
  const src = entryPhotoSrc(image);
  if (!src) return null;
  const alt = image.note?.trim() ?? '';
  const more = count !== undefined && count > 1 ? count - 1 : 0;
  return (
    <div className="rg-entry-photo" data-testid={testId}>
      <img
        src={src}
        alt={alt}
        className="rg-entry-photo__image"
        loading="lazy"
        decoding="async"
        {...(image.width && image.height ? { width: image.width, height: image.height } : {})}
      />
      {count !== undefined && count > 0 && countLabel && (
        <span className="rg-entry-photo__sr-only">{countLabel}</span>
      )}
      {more > 0 && (
        <Badge tone="neutral" className="rg-entry-photo__more" aria-hidden="true">
          +{more}
        </Badge>
      )}
    </div>
  );
}

export default EntryPhotoPreview;
